import { Box, Typography, keyframes } from "@mui/material";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

interface LoadingTransitionProps {
  isVisible?: boolean;
  onComplete?: () => void;
  initialText?: string;
  completeText?: string;
}

// Keyframe animations
const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
`;

const mexicanWave = keyframes`
  0% {
    transform: translateY(0);
  }
  5% {
    transform: translateY(-2px);
  }
  10% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(0);
  }
`;

export default function LoadingTransition({ isVisible = true, onComplete, initialText, completeText }: LoadingTransitionProps) {
  const { t, i18n } = useTranslation("loadingTransition");
  const theme = useTheme();
  const isArabic = i18n.language === "ar";

  // Use custom text if provided, otherwise use translations
  const defaultInitialText = initialText || t("initialText");
  const defaultCompleteText = completeText || t("completeText");

  const [currentText, setCurrentText] = useState(defaultInitialText);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isCompleted = currentText === defaultCompleteText;

  // Reset state when visibility changes
  useEffect(() => {
    if (isVisible) {
      setCurrentText(defaultInitialText);
      setIsTransitioning(false);
    }
  }, [isVisible, defaultInitialText]);

  // Only run timers when visible
  useEffect(() => {
    if (!isVisible) return;

    // Fade out before changing text
    const fadeOutTimer = setTimeout(() => {
      setIsTransitioning(true);
    }, 3800);

    const textTimer = setTimeout(() => {
      setCurrentText(defaultCompleteText);
      setIsTransitioning(false);
    }, 4000);

    const completeTimer = setTimeout(() => {
      onComplete?.();
    }, 6000);

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(textTimer);
      clearTimeout(completeTimer);
    };
  }, [isVisible, defaultCompleteText, onComplete]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgb(254,252,254)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "32px",
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? "visible" : "hidden",
        pointerEvents: isVisible ? "auto" : "none",
      }}
    >
      {/* Status Bar (Mobile) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "393px",
          height: "60px",
          overflow: "hidden",
          display: ["block", "none"],
        }}
      >
        {/* You can add status bar content here if needed */}
      </Box>

      {/* Content Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "32px",
        }}
      >
        {/* GIF Container */}
        <Box
          sx={{
            width: "207px",
            height: "180px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <img
            src="/images/f05927201256e224220be03d317bb16290394992.gif"
            alt="Loading animation"
            style={{
              position: "absolute",
              width: "103%",
              height: "118.23%",
              left: "5.15%",
              top: 0,
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Text Container */}
        <Box
          sx={{
            width: "338px",
            maxWidth: "90vw",
            minHeight: "72px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "24px",
          }}
        >
          <Box
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: "24px",
              textAlign: "center",
              color: "var(--font-darkest)",
              direction: isArabic ? "rtl" : "ltr",
              opacity: isTransitioning ? 0 : 1,
              transition: "opacity 0.4s ease-in-out",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "0",
            }}
          >
            {currentText.split(" ").map((word, wordIndex, words) => {
              const charsBeforeWord = words.slice(0, wordIndex).join(" ").length + (wordIndex > 0 ? wordIndex : 0);
              return (
                <Box
                  key={wordIndex}
                  component="span"
                  sx={{
                    display: "inline-flex",
                    whiteSpace: "nowrap",
                  }}
                >
                  {word.split("").map((char, charIndex) => {
                    const globalIndex = charsBeforeWord + charIndex;
                    return (
                      <Box
                        key={charIndex}
                        component="span"
                        sx={{
                          display: "inline-block",
                          // animation: `${mexicanWave} 1.2s ease-in-out infinite`,
                          // animationDelay: `${globalIndex * 0.015}s`,
                        }}
                      >
                        {char}
                      </Box>
                    );
                  })}
                  {wordIndex < words.length - 1 && "\u00A0"}
                </Box>
              );
            })}
          </Box>

          {/* Animated Loading Dots */}
          <Box
            sx={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "var(--secondary-default)",
                  animation: `${bounce} 1.4s infinite ease-in-out`,
                  animationDelay: `${index * 0.16}s`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
